/**
 * Objednavky sealed Delivery Policy snapshot (G11) — independent of late-CTA visibility.
 * Pickup modality must never render this panel.
 */
import { useTranslation } from 'react-i18next';
import {
  isDeliveryPolicyDisplayReady,
  resolveDeliveryPolicyBlob,
} from '../../checkout/deliveryPolicyLocale.js';
import type { PolicyLocalizedBlob } from '../../checkout/shippingCheckoutApi.js';

export interface SealedShippingPolicySnapshot {
  readonly etaMinBusinessDays?: number | null;
  readonly etaMaxBusinessDays?: number | null;
  readonly latestDeliveryBusinessDays?: number | null;
  readonly priceRangeDescription?: PolicyLocalizedBlob | null;
  readonly outOfBandFeeWarning?: PolicyLocalizedBlob | null;
  readonly mechanicsDescription?: PolicyLocalizedBlob | null;
  readonly promisedDeliveryByAt?: string | null;
  readonly autoDeliverEligibleAt?: string | null;
}

function asSnapshot(raw: unknown): SealedShippingPolicySnapshot | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  return raw as SealedShippingPolicySnapshot;
}

export interface SealedShippingPolicyPanelProps {
  readonly fulfillmentMode: 'PICKUP' | 'SHIPPING' | string | null | undefined;
  readonly shippingMethodSnapshot?: unknown | null;
  readonly promisedDeliveryByAt?: string | null;
  readonly autoDeliverEligibleAt?: string | null;
  readonly tenantHandoffKind?:
    | 'DISPATCHED_TO_CARRIER'
    | 'ARRIVED_AT_PARCEL_PICKUP_POINT'
    | 'CUSTOMER_COLLECTED_ASSERTED_BY_TENANT'
    | null;
  readonly status?: string | null;
  readonly formatDate?: (iso: string) => string;
  /** Injected clock for tests; default fresh Date.now() each render (G14). */
  readonly nowMs?: number;
}

export function SealedShippingPolicyPanel({
  fulfillmentMode,
  shippingMethodSnapshot,
  promisedDeliveryByAt,
  autoDeliverEligibleAt: _autoDeliverEligibleAt,
  tenantHandoffKind: _tenantHandoffKind,
  status,
  formatDate,
  nowMs: nowMsProp,
}: SealedShippingPolicyPanelProps): JSX.Element | null {
  const { t, i18n } = useTranslation('customer');
  if (fulfillmentMode !== 'SHIPPING') {
    return null;
  }

  // G14 — fresh now (parity with late-CTA Date.now()); never freeze mount time.
  const nowMs = nowMsProp ?? Date.now();

  const snap = asSnapshot(shippingMethodSnapshot);
  const uiLanguage = i18n.resolvedLanguage ?? i18n.language;
  const policyLike =
    snap != null
      ? {
          etaMinBusinessDays: snap.etaMinBusinessDays ?? Number.NaN,
          etaMaxBusinessDays: snap.etaMaxBusinessDays ?? Number.NaN,
          latestDeliveryBusinessDays: snap.latestDeliveryBusinessDays ?? Number.NaN,
          priceRangeDescription: snap.priceRangeDescription ?? {},
          outOfBandFeeWarning: snap.outOfBandFeeWarning ?? {},
          mechanicsDescription: snap.mechanicsDescription ?? {},
        }
      : null;
  const ready = isDeliveryPolicyDisplayReady(policyLike, uiLanguage);
  const deadlineIso =
    promisedDeliveryByAt ??
    (typeof snap?.promisedDeliveryByAt === 'string' ? snap.promisedDeliveryByAt : null);
  const deadlineMs = deadlineIso != null ? Date.parse(deadlineIso) : Number.NaN;
  const preDeadline =
    Number.isFinite(deadlineMs) &&
    nowMs <= deadlineMs &&
    status !== 'DELIVERED' &&
    status !== 'CANCELED' &&
    status !== 'CANCELLED';

  if (!ready && deadlineIso == null) {
    return (
      <p
        className="m-0 text-sm text-[var(--color-on-surface-muted)]"
        data-testid="shipping-sealed-policy-missing"
      >
        {t('account.orders.shipping.policySnapshotUnavailable')}
      </p>
    );
  }

  const priceRange =
    ready && policyLike != null
      ? resolveDeliveryPolicyBlob(policyLike.priceRangeDescription, uiLanguage)
      : null;
  const warning =
    ready && policyLike != null
      ? resolveDeliveryPolicyBlob(policyLike.outOfBandFeeWarning, uiLanguage)
      : null;
  const mechanics =
    ready && policyLike != null
      ? resolveDeliveryPolicyBlob(policyLike.mechanicsDescription, uiLanguage)
      : null;

  return (
    <section
      className="flex flex-col gap-2 rounded-lg border border-[var(--color-border)] p-4 text-sm"
      data-testid="shipping-sealed-policy"
    >
      <h3 className="m-0 text-base font-semibold">
        {t('account.orders.shipping.policySnapshotTitle')}
      </h3>
      {ready && policyLike != null ? (
        <>
          <p className="m-0" data-testid="shipping-sealed-eta">
            {t('checkout.shipping.etaRange', {
              min: policyLike.etaMinBusinessDays,
              max: policyLike.etaMaxBusinessDays,
            })}
          </p>
          <p className="m-0" data-testid="shipping-sealed-deadline-days">
            {t('checkout.shipping.deadlineBusinessDays', {
              days: policyLike.latestDeliveryBusinessDays,
            })}
          </p>
          {priceRange != null ? <p className="m-0">{priceRange}</p> : null}
          {warning != null ? (
            <p className="m-0 text-[var(--color-on-surface-muted)]">{warning}</p>
          ) : null}
          {mechanics != null ? <p className="m-0">{mechanics}</p> : null}
        </>
      ) : (
        <p className="m-0 text-[var(--color-danger)]" role="alert">
          {t('account.orders.shipping.policySnapshotUnavailable')}
        </p>
      )}
      {deadlineIso != null ? (
        <p className="m-0" data-testid="shipping-sealed-promised-by">
          {t('account.orders.shipping.promisedDeliveryBy', {
            when:
              formatDate != null && Number.isFinite(deadlineMs)
                ? formatDate(deadlineIso)
                : deadlineIso,
          })}
        </p>
      ) : null}
      {preDeadline ? (
        <p
          className="m-0 text-[var(--color-on-surface-muted)]"
          data-testid="shipping-order-advance-promise"
        >
          {t('account.orders.shipping.lateReportAdvancePromise')}
        </p>
      ) : null}
    </section>
  );
}
